import { Badge, Button, Grid, Header, SpaceBetween, Spinner } from '@cloudscape-design/components';
import { useBreadcrumbs } from 'contexts/BreadcrumbContext';
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { faBuilding, faGem, faObjectUngroup, faCompass } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CardSummary from 'components/shared/CardSummary';
import { useNavigate } from 'react-router-dom';
import { helpPanelDefaultContent, useHelpPanel } from 'contexts/HelpPanelContext';
import deploymentService from 'services/deploymentService';
import organizationService from 'services/organizationService';
import { useUserContext } from 'contexts/UserContext';
import { DeploymentItem } from 'types/deploymentTypes';
import { OrganizationDetails } from 'types/organizationTypes';
import UserService from 'services/UserService';
import PERMISSIONS from 'configs/Permissions';
import { disabledButtonReason } from 'configs/settings';
import AppConfig from 'configs/AppConfig';

const CustomBadge = ({icon, text, className, onClick}:{icon:ReactNode, text:string, className?:string, onClick?:()=>void})=>{
    return (
        <div onClick={onClick}>
            <Badge color='blue' className={className}>
                {icon} {" "}
                {text}
            </Badge>
        </div>
    )
}


function Dashboard() {

    const { setBreadcrumbs } = useBreadcrumbs();
    const { setHelpPanelContent } = useHelpPanel();
    const { userId } = useUserContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const navigate = useNavigate();
    const [sharedDeployments, setSharedDeployments] = useState<DeploymentItem[]>([]);
    const [dedicatedClusters, setDedicatedClusters] = useState<DeploymentItem[]>([]);
    const [organizations, setOrganizations] = useState<OrganizationDetails[] | null>(null);    


    const fetchDashboardData = async()=>{
        try {
            setLoading(true);
            setError(false);
            
            const dedicatedClustersResponse = await deploymentService.fetchDedicatedClusters();
            setDedicatedClusters(dedicatedClustersResponse);
            
            const sharedDeploymentsResponse = await deploymentService.fetchSharedDeployments();
            setSharedDeployments(sharedDeploymentsResponse);

            const organizationsResponse = await organizationService.getOrganizations();
            setOrganizations(organizationsResponse)
            
        } catch (error) {
            console.error("Failed to load dashboard data");
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    const handleCreateDedicatedClusterClick = () => {
        const targetUrl = "/dedicated-clusters/create";

        if (typeof (window as any).gtag_report_conversion === "function") {
            (window as any).gtag_report_conversion(window.location.origin + targetUrl);
        } else {
            navigate(targetUrl);
        }
    };


    const { totalSharedDeployments, ownedSharedDeployments, notOwnedSharedDeployments } = useMemo(() => {
        const ownedSharedDeployments = sharedDeployments.filter(deployment => deployment.creator_id === userId).length;
        const totalSharedDeployments = sharedDeployments.length;
        const notOwnedSharedDeployments = totalSharedDeployments - ownedSharedDeployments

        return {
            totalSharedDeployments,
            ownedSharedDeployments,
            notOwnedSharedDeployments
        };
    }, [sharedDeployments, userId]);
    

    const { totalDedicatedClusters, ownedDedicatedClusters, notOwnedDedicatedClusters } = useMemo(() => {
        const ownedDedicatedClusters = dedicatedClusters.filter(deployment => deployment.creator_id === userId).length;
        const totalDedicatedClusters = dedicatedClusters.length;
        const notOwnedDedicatedClusters = totalDedicatedClusters - ownedDedicatedClusters

        return {
            totalDedicatedClusters,
            ownedDedicatedClusters,
            notOwnedDedicatedClusters
        };
    }, [dedicatedClusters, userId]);


    useEffect(()=>{
        fetchDashboardData();
    },[])


    useEffect(() => {
        setBreadcrumbs([
          { text: AppConfig.APP_NAME, href: "/" },
          { text: "Dashboard", href: "/dashboard" },
        ]);
    
        //   Cleanup when component unmounts
        return () => {
          setBreadcrumbs([]);
        };
    }, [setBreadcrumbs]);


    useEffect(() => {
        setHelpPanelContent(helpPanelDefaultContent);
    }, [setHelpPanelContent]);


  return (
    <div>
        <SpaceBetween size="l">
            <div className="header-container">
                <Header variant="h2">
                    <FontAwesomeIcon
                        icon={faCompass}
                        className="building-icon"
                    />{" "}
                    Your Infrastructure
                </Header>
            </div>
            
            <div className="right-header-container">
                {/* <Button 
                    variant='primary'
                    disabled={!UserService.hasRole([PERMISSIONS.DEPLOYMENTS.MANAGE_CLUSTERS])}
                    disabledReason={disabledButtonReason}
                    onClick={()=>navigate("/shared-deployments/create")}
                >
                    <span className='nowarp-text'>Create Shared Deployment</span>
                </Button> */}

                <Button 
                    variant='primary'
                    disabled={!UserService.hasRole([PERMISSIONS.DEPLOYMENTS.MANAGE_CLUSTERS])}
                    disabledReason={disabledButtonReason}
                    onClick={handleCreateDedicatedClusterClick}
                >
                    <span className='nowarp-text'>Create Dedicated Cluster</span>
                </Button>
            </div>

            {loading ? (
                <>
                    <Spinner size="large" />
                    <span>Loading dashboard data...</span>
                </>
            ):error ? (
                <span>Failed to fetch dashboard data !</span>
            ):(
                <Grid
                    gridDefinition={[
                    { colspan: { default: 12, xs: 4 } },
                    { colspan: { default: 12, xs: 4 } },
                    { colspan: { default: 12, xs: 4 } },
                    ]}
                >
                    {/* <CardSummary
                        className='clickable'
                        onClick={()=>navigate("/shared-deployments")}
                        icon={<CustomBadge icon={<FontAwesomeIcon icon={faObjectUngroup} />} text='Shared Deployments' />}
                        boldText={totalSharedDeployments.toString()}
                        text={totalSharedDeployments === 0 ? "Your shared realm." : ownedSharedDeployments === 1 ? "You own 1 shared deployment." : `You own ${ownedSharedDeployments} shared deployments.`}
                        lightText={notOwnedSharedDeployments === 0 ? "Deploy a shared realm." : notOwnedSharedDeployments === 1 ? "You are a member of 1 shared deployment." : `You are a member of ${notOwnedSharedDeployments} shared deployments.`}
                        buttonPosition='right'
                        buttonIcon="arrow-right"
                    /> */}

                    <CardSummary
                        className='clickable'
                        onClick={()=>navigate("/dedicated-clusters")}
                        icon={<CustomBadge icon={<FontAwesomeIcon icon={faGem} />} text='Dedicated Clusters' />}
                        boldText={totalDedicatedClusters.toString()} 
                        text={totalDedicatedClusters === 0 ? "Your dedicated infrastructure." : ownedDedicatedClusters === 1 ? "You own 1 dedicated cluster." : `You own ${ownedDedicatedClusters} dedicated clusters.`}
                        lightText={notOwnedDedicatedClusters === 0 ? "Deploy multiple realm into the cluster." : notOwnedDedicatedClusters === 1 ? "You are a member of 1 dedicated cluster." : `You are a member of ${notOwnedDedicatedClusters} dedicated clusters.`}
                        buttonPosition='right'
                        buttonIcon="arrow-right"
                    />

                    <CardSummary
                        className='clickable'
                        onClick={()=>{
                            if(organizations?.length === 1) navigate(`/organizations/${organizations[0].id}`)
                            else navigate("/organizations")
                        }}
                        icon={<CustomBadge icon={<FontAwesomeIcon icon={faBuilding} />} text='Organizations' />}
                        boldText={organizations?.length.toString() ?? "0"} 
                        text="You are a part of 1 organization." 
                        lightText="Manage members and roles." 
                        buttonPosition='right'
                        buttonIcon="arrow-right"
                    />
                </Grid>
            )}

        </SpaceBetween>
    </div>
  )
}

export default Dashboard
